import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { Loader2, Sparkles, Filter } from 'lucide-react';
import { useCatalogsStore } from '@/stores/catalogs';
import type { ProposedAdTaxonomyElement } from '@/services/api';

const DIMENSION_COLORS: Record<string, string> = {
  form: 'bg-sky-50 text-sky-700 border-sky-200',
  meaning: 'bg-violet-50 text-violet-700 border-violet-200',
};

export function ProposedAdTaxonomyList() {
  const {
    adTaxonomyProposals,
    adTaxonomyProposalsLoading,
    fetchAdTaxonomyProposals,
  } = useCatalogsStore();

  const [dimension, setDimension] = useState<string>('');
  const [category, setCategory] = useState<string>('');

  useEffect(() => {
    fetchAdTaxonomyProposals();
  }, [fetchAdTaxonomyProposals]);

  const categories = useMemo(() => {
    const set = new Set<string>();
    adTaxonomyProposals.forEach((p) => set.add(p.category));
    return Array.from(set).sort();
  }, [adTaxonomyProposals]);

  const filtered = adTaxonomyProposals.filter((p) => {
    if (dimension && p.dimension !== dimension) return false;
    if (category && p.category !== category) return false;
    return true;
  });

  if (adTaxonomyProposalsLoading && adTaxonomyProposals.length === 0) {
    return (
      <div className="text-sm text-slate-400 text-center py-8">
        <Loader2 size={14} className="animate-spin inline mr-2" /> Loading proposals...
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={12} className="text-slate-400" />
        <select
          value={dimension}
          onChange={(e) => setDimension(e.target.value)}
          className="text-xs bg-white border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        >
          <option value="">All dimensions</option>
          <option value="form">Form</option>
          <option value="meaning">Meaning</option>
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="text-xs bg-white border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <span className="text-xs text-slate-500 ml-auto">
          {filtered.length} of {adTaxonomyProposals.length}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <Sparkles size={24} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-500">
            No proposed Ad Taxonomy elements for the current filters.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Dynamically-discovered elements surface here when the ad-extraction
            pipeline adds nodes not in the curated seed taxonomy.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {filtered.map((p) => (
            <Row key={p.id} element={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ element: p }: { element: ProposedAdTaxonomyElement }) {
  return (
    <div className="px-4 py-3 flex items-start gap-3">
      <span
        className={clsx(
          'text-[10px] font-bold uppercase px-1.5 py-0.5 rounded border',
          DIMENSION_COLORS[p.dimension] ?? 'bg-slate-50 text-slate-600 border-slate-200',
        )}
      >
        {p.dimension}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-navy-900">
            {p.display_name}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {p.category}
          </span>
          <span className="text-[10px] text-slate-400">via {p.derived_from}</span>
        </div>
        {p.description && (
          <p className="text-xs text-slate-600 mt-1 line-clamp-2">{p.description}</p>
        )}
        {p.synonyms && p.synonyms.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {p.synonyms.slice(0, 5).map((s) => (
              <span
                key={s}
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-50 text-slate-500"
              >
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end text-[10px] text-slate-500 flex-shrink-0">
        {p.instance_count > 0 && <span>{p.instance_count} instances</span>}
        {p.avg_performance_tier && (
          <span className="uppercase font-medium mt-0.5">
            {p.avg_performance_tier.replace(/_/g, ' ')}
          </span>
        )}
        {p.confidence_score != null && (
          <span className="text-emerald-600 font-semibold mt-0.5">
            {(p.confidence_score * 100).toFixed(0)}%
          </span>
        )}
      </div>
    </div>
  );
}
